import { Select, SelectMenuItem } from '@execonline-inc/execonline-ui';
import { TranslationsContext } from '@execonline-inc/translations';
import { Search } from 'lucide-react';
import { fromEmpty, Maybe, nothing } from 'maybeasy';
import { observer } from 'mobx-react';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { T, translation } from '../../../../../../Translations';
import { participantSearchStore } from '../Stores/ParticipantSearchStore';
import { searchOptionsStore } from '../Stores/SearchOptionsStore';

// The Select carries the license id as a string; an empty string is "All Licenses".
const ALL_LICENSES = '';

const PeopleSearchFilters: React.FC = () => {
  const ts = React.useContext(TranslationsContext);
  const [selectedLicense, setSelectedLicense] = useState<Maybe<number>>(nothing());
  const [localSearchValue, setLocalSearchValue] = useState(
    participantSearchStore.participantNameSearch,
  );
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const licenses = searchOptionsStore.licenses;

  const licenseName = (value: string): React.ReactNode =>
    fromEmpty(value)
      .map((id) => parseInt(id, 10))
      .andThen((id) => fromEmpty(licenses.filter((l) => l.id === id)))
      .map(([license]) => <>{license.licenseTypeName}</>)
      .getOrElse(() => <T kind="All Licenses" />);

  const handleLicenseChange = (value: string) => {
    if (value === ALL_LICENSES) {
      setSelectedLicense(nothing());
      participantSearchStore.clearSelectedLicenseId();
    } else {
      setSelectedLicense(fromEmpty(value).map((id) => parseInt(id, 10)));
      participantSearchStore.setSelectedLicenseId(value);
    }
  };

  // Debounce search input
  const handleSearchChange = (value: string) => {
    setLocalSearchValue(value);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // If search is cleared, update immediately without debouncing
    if (value.trim() === '') {
      participantSearchStore.setParticipantNameSearch('');
      return;
    }

    debounceTimerRef.current = setTimeout(() => {
      participantSearchStore.setParticipantNameSearch(value);
    }, 300);
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const selectedValue = selectedLicense.map((id) => id.toString()).getOrElseValue(ALL_LICENSES);

  return (
    <div className="flex gap-4 mb-4" data-intercom-target="participant-search-people-filters">
      <div className="flex-1 relative">
        <input
          type="text"
          placeholder={translation('Search Name or Email')(ts)}
          className="w-full px-4 py-2 pl-10 border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
          value={localSearchValue}
          onChange={(e) => handleSearchChange(e.target.value)}
          data-intercom-target="participant-search-name-input"
        />
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
      </div>
      {/* Always shown, as the license dropdown was before; with no licenses it offers only All Licenses. */}
      <Select
        multiple={false}
        value={selectedValue}
        onChange={handleLicenseChange}
        renderValue={licenseName}
        data-intercom-target="participant-search-license-select"
      >
        <SelectMenuItem value={ALL_LICENSES} selected={selectedValue === ALL_LICENSES}>
          <T kind="All Licenses" />
        </SelectMenuItem>
        {licenses.map((license) => (
          <SelectMenuItem
            key={license.id}
            value={license.id.toString()}
            selected={selectedValue === license.id.toString()}
          >
            {license.licenseTypeName}
          </SelectMenuItem>
        ))}
      </Select>
    </div>
  );
};

export default observer(PeopleSearchFilters);
